// @flow
import { parseQuery } from 'app/packages/url';
import { type InstallPromptEventGetter } from 'app/common/react/ui/BeforeInstallPromptEventContext';

export type PwaType = 'unknown' | 'windows' | 'chrome';
export type PWAUpsellEligibilityType = 'windows' | 'chrome' | 'notEligible';
export type InstallEvent = {
  preventDefault: () => void,
  prompt: () => void,
  userChoice: Promise<{ outcome: 'dismissed' | 'accepted' }>,
};

export default function pwaType(win: any): PwaType | null {
  if (!win) {
    return null;
  }

  // This is the namespace that's made available when in PWA. See link for more info.
  // https://docs.microsoft.com/en-us/microsoft-edge/progressive-web-apps/windows-features#find-windows-runtime-winrt-apis
  if (win.Windows) {
    return 'windows';
  }

  const isPWA =
    win.location &&
    win.location.search &&
    parseQuery(win.location.search).utm_source === 'homescreen_icon';
  if (
    isPWA &&
    win.navigator &&
    win.navigator.userAgent &&
    win.navigator.userAgent.includes('Chrome') &&
    // https://developers.google.com/web/fundamentals/app-install-banners/#display-mode_media_query
    win.matchMedia('(display-mode: standalone)').matches
  ) {
    return 'chrome';
  }

  return 'unknown';
}

// function to return true if the user is currently on a PWA
export function checkUsingPwa(win: any): boolean {
  const currentPwaType = pwaType(win);
  return !!(currentPwaType && currentPwaType !== 'unknown');
}

export function getPWAUpsellEligibility(
  win: any,
  installPromptEventGetter?: ?InstallPromptEventGetter,
) {
  if (typeof win === 'undefined') {
    return 'notEligible';
  }

  const currentlyUsingPWA = checkUsingPwa(win);

  if (!currentlyUsingPWA && win.navigator && win.navigator.userAgent) {
    // if on a Windows PWA-eligible device, only check for that (better support than Chrome PWA)
    if (win.navigator.userAgent.includes('Windows NT 10.0')) {
      return 'windows';
    } else if (installPromptEventGetter && installPromptEventGetter()) {
      return 'chrome';
    }
  }
  return 'notEligible';
}
